import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import secureLocalStorage from "react-secure-storage";
import { USER } from "../../constants/storage";

import { UserDataType, UserStateType, UserType } from "../../types/user";
import type { RootState } from "../../types/store";
import { LOG_OUT } from "../../components/SignIn/signIn.gql";
import { ApolloClientType } from "../../types/apollo";
import {
  GET_FINISHED_APPOINTMENTS_COUNT,
  GET_NOTIFICATIONS, patientsQuery, userBlockedPeriodsQuery, userQuery
} from "../../queries/user.qql";
import { userAppointmentsData, userAppointmentsQuery } from "../../queries/appointments.gql";

const getUser = () => {
  return secureLocalStorage.getItem(USER) as UserType | null;
};

const initialState: UserStateType = {
  id: getUser()?.id ?? null,
  isSignedInState: !!getUser()?.id && !!getUser()?.role,
  role: getUser()?.role ?? null,
  loading: false,
  data: null,
  availabilities: null,
  blocked: null,
  appointments: [],
  appointmentsLoading: false,
  currentDoctor: null,
  notifications: 0,
  patients: [],
  patientsLoading: false,
  assistantModalOpen: false,
  finishedAppointmentWithCurrentDoctor: 0
};

export const fetchUser = createAsyncThunk(
  "user/fetch",
  async (_, { getState, extra }) => {
    const { apolloClient } = extra as { apolloClient: ApolloClientType };
    const { user } = getState() as RootState;
    let countOfAppointment = 0;

    const { data } = await apolloClient.query({
      query: userQuery[user.role!],
      variables: {
        id: Number(user.id)
      },
      fetchPolicy: "no-cache"
    });

    if ("patient" in data) {
      const currentDoctor = data.patient.oncologist?.at(-1) ?? data.patient.nutrizionist?.at(-1);

      const { data: finishedAppointmentsData } = await apolloClient.query({
        query: GET_FINISHED_APPOINTMENTS_COUNT,
        variables: {
          id: Number(currentDoctor.id)
        },
        fetchPolicy: "no-cache"
      });

      if (finishedAppointmentsData.finishedAppointmentsCount) {
        countOfAppointment = finishedAppointmentsData.finishedAppointmentsCount;
      }
    }

    return { ...data[user.role!], countOfAppointment };
  },
);

export const getNotifications = createAsyncThunk(
  "notifications/fetch",
  async (_, { extra }) => {
    const { apolloClient } = extra as { apolloClient: ApolloClientType };

    const { data } = await apolloClient.query({
      query: GET_NOTIFICATIONS,
      fetchPolicy: "no-cache"
    });

    return data.notifications.length;
  },
);

export const getPatients = createAsyncThunk(
  "patients/fetch",
  async (_, { getState, extra }) => {
    const { apolloClient } = extra as { apolloClient: ApolloClientType };
    const { user } = getState() as RootState;

    const { data } = await apolloClient.query({
      query: patientsQuery[user.role as "oncologist" | "nutrizionist"],
      variables: {
        id: Number(user.id)
      },
      fetchPolicy: "no-cache"
    });

    return { ...data[user.role!] };
  },
);

export const fetchBlockedPeriods = createAsyncThunk(
  "user/blockedPeriods/fetch",
  async (_, { getState, extra }) => {
    const { apolloClient } = extra as { apolloClient: ApolloClientType };
    const { user } = getState() as RootState;

    const { data } = await apolloClient.query({
      query: userBlockedPeriodsQuery[user.role!],
      variables: {
        id: Number(user.id)
      },
      fetchPolicy: "no-cache"
    });

    return { ...data[user.role!] };
  },
);

export const fetchAppointments = createAsyncThunk(
  "user/appointments/fetch",
  async (_, { getState, extra }) => {
    const { apolloClient } = extra as { apolloClient: ApolloClientType };
    const { user } = getState() as RootState;

    const { data } = await apolloClient.query({
      query: userAppointmentsQuery[user.role!],
      fetchPolicy: "no-cache"
    });

    return data[userAppointmentsData[user.role!]];
  },
);

export const logOut = createAsyncThunk<void, void>(
  "user/logOut",
  async (_, { extra }): Promise<void> => {
    const { apolloClient } = extra as { apolloClient: ApolloClientType };

    await apolloClient.mutate({
      mutation: LOG_OUT,
    });

    await apolloClient.clearCacheData(apolloClient);
  },
);

export const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    toggleAssistantModal(state) {
      state.assistantModalOpen = !state.assistantModalOpen;
    },
    setUser(state, action: PayloadAction<UserType>) {
      const { id, role } = action.payload;
      secureLocalStorage.setItem(USER, { id, role });

      const user = getUser();
      if (user !== null) {
        state.id = user.id;
        state.role = user.role;
        state.isSignedInState = true;
      }
    },
    logOutAction(state) {
      secureLocalStorage.removeItem(USER);
      state.id = null;
      state.isSignedInState = false;
      state.role = null;
      state.loading = false;
      state.data = null;
      state.availabilities = null;
      state.blocked = null;
      state.appointments = [];
      state.appointmentsLoading = false;
      state.currentDoctor = null;
      state.notifications = 0;
      state.patients = [];
      state.finishedAppointmentWithCurrentDoctor = 0;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchUser.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getNotifications.fulfilled, (state, action) => {
      state.notifications = action.payload;
    });
    builder.addCase(getPatients.pending, (state) => {
      state.patientsLoading = true;
    });
    builder.addCase(getPatients.fulfilled, (state, action) => {
      state.patientsLoading = false;
      state.patients = action.payload.patients;
    });
    builder.addCase(fetchUser.fulfilled, (state, action) => {
      const {
        availabilities, blockedPeriods, countOfAppointment, ...rest
      } = action.payload as UserDataType;
      state.blocked = blockedPeriods;
      state.availabilities = availabilities;
      state.finishedAppointmentWithCurrentDoctor = countOfAppointment;
      state.data = rest as UserDataType;

      if (state.role === "patient") {
        state.currentDoctor = rest.oncologist?.at(-1) ?? rest.nutrizionist?.at(-1);
      }

      state.loading = false;
    });
    builder.addCase(fetchBlockedPeriods.pending, () => {
      // state.loading = true;
      // @TODO: add loading state for blocked periods
    });
    builder.addCase(fetchBlockedPeriods.fulfilled, (state, action) => {
      const { blockedPeriods } = action.payload as UserDataType;
      state.blocked = blockedPeriods;
      // state.loading = false;
    });
    builder.addCase(fetchAppointments.pending, (state) => {
      state.appointmentsLoading = true;
    });
    builder.addCase(fetchAppointments.fulfilled, (state, action) => {
      state.appointments = action.payload;
      state.appointmentsLoading = false;
    });
    builder.addCase(logOut.pending, (state) => {
      state.loading = true;
    });
    builder.addMatcher(
      (action) => action.type === logOut.fulfilled.type
        || action.type === logOut.rejected.type,
      (state) => {
        userSlice.caseReducers.logOutAction(state);
        state.loading = false;
      }
    );
  },
});

export const {
  setUser,
  logOutAction,
  toggleAssistantModal
} = userSlice.actions;

export const isUserLoading = (state: RootState) => state.user.loading;
export const userData = (state: RootState) => state.user.data;
export const userAppointments = (state: RootState) => state.user.appointments;
export const userAppointmentsLoading = (state: RootState) => state.user.appointmentsLoading;
export const userId = (state: RootState) => state.user.id;
export const userRole = (state: RootState) => state.user.role;
export const currentDoctor = (state: RootState) => state.user.currentDoctor;
export const doctorAvailabilities = (state: RootState) => state.user.availabilities;
export const doctorBlockedPeriods = (state: RootState) => state.user.blocked;
export const isSignedInState = (state: RootState) => state.user.isSignedInState;
export const notifications = (state: RootState) => state.user.notifications;
export const patients = (state: RootState) => state.user.patients;
export const patientsLoading = (state: RootState) => state.user.patientsLoading;
export const assistantModalOpen = (state: RootState) => state.user.assistantModalOpen;
